import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { useAsync, StatCard, Table, Loading, ErrorBox, Empty, Field, fmtDate, useAction, NOTIFICATION_TYPES } from "./AdminUI";

const STAT_LABELS = [
  ["totalUsers", "Total Users"],
  ["completedProfiles", "Completed Profiles"],
  ["pendingApprovals", "Pending Approvals"],
  ["approvedLive", "Approved / Live Profiles"],
  ["rejectedProfiles", "Rejected Profiles"],
  ["paidRegistrations", "₹500 Paid Registrations"],
  ["pendingPayments", "₹500 Pending Payments"],
  ["feeWaived", "Fee-Waived Profiles"],
  ["newInterests", "New Interests"],
  ["mutualInterests", "Mutual Interests"],
  ["activeMeetings", "Active Meetings"],
  ["openDiscussions", "Open Discussions"],
  ["openReports", "Reports / Complaints"],
  ["successfulMatches", "Successful Matches"],
];

export const AdminDashboard = () => {
  const { data, loading, error } = useAsync(() => api.get("/admin/dashboard"), []);
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const { stats, recentActivity, recentUsers } = data;
  return (
    <>
      <div className="ap-stats">
        {STAT_LABELS.map(([key, label]) => (
          <StatCard key={key} label={label} value={stats[key] ?? 0} testId={`ap-dash-${key}`} />
        ))}
      </div>
      <div className="ap-card" style={{ marginTop: 20 }}>
        <h3 className="ap-card-title">Recent Admin Activity</h3>
        {recentActivity.length === 0 ? (
          <Empty>No admin actions recorded yet.</Empty>
        ) : (
          <Table columns={["When", "Admin", "Action", "Target", "Details"]}>
            {recentActivity.map((a, i) => (
              <tr key={i}>
                <td>{fmtDate(a.created_at)}</td>
                <td>{a.admin_email}</td>
                <td>{a.action}</td>
                <td>{a.target_collection} {a.target_id}</td>
                <td>{a.details}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      <div className="ap-card">
        <h3 className="ap-card-title">Newest Registered Users</h3>
        <Table columns={["Name", "Email", "Registered"]}>
          {recentUsers.map((u) => (
            <tr key={u.id}>
              <td>{u.fullName || "—"}</td>
              <td>{u.email}</td>
              <td>{fmtDate(u.createdAt)}</td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  );
};

export const AdminAnalytics = () => {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const { data, loading, error, reload } = useAsync(
    () => api.get(`/admin/analytics?from=${from}&to=${to}`),
    [from, to],
  );
  return (
    <>
      <div className="ap-card">
        <div className="ap-filters">
          <div><label className="ap-label">From</label><input type="date" className="ap-input" data-testid="ap-analytics-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label className="ap-label">To</label><input type="date" className="ap-input" data-testid="ap-analytics-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <button className="btn btn-solid btn-sm" onClick={reload}>Refresh</button>
        </div>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : (
          <Table columns={["Metric", "Total in range", "Trend"]}>
            {data.metrics.map((m) => {
              const max = Math.max(1, ...m.series.map((s) => s.count));
              return (
                <tr key={m.key}>
                  <td>{m.label}</td>
                  <td data-testid={`ap-analytics-${m.key}`}>{m.total}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 34 }}>
                      {m.series.map((s) => (
                        <div key={s.date} className="ap-bar" style={{ height: `${(s.count / max) * 30}px`, width: 6, opacity: s.count ? 1 : 0.25 }} title={`${s.date}: ${s.count}`} />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </div>
    </>
  );
};

export const AdminSettings = () => {
  const { user } = useAuth();
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/settings"), []);
  const { busy, err, run } = useAction();
  const [form, setForm] = useState(null);
  useEffect(() => { if (data?.settings && form === null) setForm(data.settings); }, [data, form]);
  if (loading || !form) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const isSuper = user?.role === "super_admin";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="ap-card">
      <h3 className="ap-card-title">Site Settings</h3>
      {err && <ErrorBox error={err} />}
      <div className="ap-row">
        <Field label="Site Name"><input className="ap-input" data-testid="ap-setting-site-name" value={form.site_name} onChange={(e) => set("site_name", e.target.value)} disabled={!isSuper} /></Field>
        <Field label="Contact Email"><input className="ap-input" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} disabled={!isSuper} /></Field>
      </div>
      <div className="ap-row">
        <Field label="Contact Phone"><input className="ap-input" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} disabled={!isSuper} /></Field>
        <Field label="₹500 Registration Fee (₹)"><input type="number" className="ap-input" value={form.registration_fee} onChange={(e) => set("registration_fee", e.target.value)} disabled={!isSuper} /></Field>
      </div>
      <div className="ap-row">
        <Field label="Successful Match Fee Display"><input className="ap-input" value={form.match_fee_display} onChange={(e) => set("match_fee_display", e.target.value)} disabled={!isSuper} /></Field>
        <Field label="Profile Approval Mode">
          <select className="ap-select" data-testid="ap-setting-approval-mode" value={form.profile_approval_mode} onChange={(e) => set("profile_approval_mode", e.target.value)} disabled={!isSuper}>
            <option value="auto">Auto-approve on submit</option>
            <option value="manual">Manual admin review</option>
          </select>
        </Field>
      </div>
      <div className="ap-row">
        <Field label="Notifications Enabled">
          <select className="ap-select" value={String(form.notifications_enabled)} onChange={(e) => set("notifications_enabled", e.target.value === "true")} disabled={!isSuper}>
            <option value="true">Yes</option><option value="false">No</option>
          </select>
        </Field>
        <Field label="Discussion Board Enabled">
          <select className="ap-select" value={String(form.discussions_enabled)} onChange={(e) => set("discussions_enabled", e.target.value === "true")} disabled={!isSuper}>
            <option value="true">Yes</option><option value="false">No</option>
          </select>
        </Field>
      </div>
      <p className="ap-note" style={{ marginBottom: 12 }}>
        {isSuper ? "Only a Super Admin can change site settings. Business rules are never changed automatically." : "Read-only — ask a Super Admin to change settings."}
      </p>
      {isSuper && (
        <button
          type="button" className="btn btn-solid btn-sm" data-testid="ap-settings-save" disabled={busy}
          onClick={() => run(() => api.put("/admin/settings", form), () => { toast.success("Settings saved."); reload(); })}
        >
          {busy ? "Saving…" : "Save Settings"}
        </button>
      )}
    </div>
  );
};

const CONTENT_SECTIONS = [
  ["terms", "Terms & Conditions"], ["privacy", "Privacy Policy"], ["safety", "Safety & Community Guidelines"],
  ["payment", "Payment & Refund Policy"], ["match_fee", "Successful Match Service Fee Terms"],
  ["faq", "FAQ"], ["about", "About Suffa"], ["contact", "Contact Information"],
];

export const AdminContent = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/content"), []);
  const { busy, err, run } = useAction();
  const [key, setKey] = useState("faq");
  const [form, setForm] = useState({ title: "", body: "" });

  useEffect(() => {
    if (data?.content) {
      const existing = data.content[key];
      setForm({ title: existing?.title || "", body: existing?.body || "" });
    }
  }, [data, key]);

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const existing = data.content[key];

  return (
    <div className="ap-card">
      <h3 className="ap-card-title">Legal &amp; Content Management</h3>
      <p className="ap-note" style={{ marginBottom: 14 }}>
        Existing approved legal pages stay untouched until you explicitly save an override here. Sections without an override keep their approved live content.
      </p>
      <div className="ap-tabs" data-testid="ap-content-tabs">
        {CONTENT_SECTIONS.map(([k, label]) => (
          <button key={k} type="button" className={`ap-tab${k === key ? " is-active" : ""}`} onClick={() => setKey(k)}>
            {label}{existing ? "" : k === key ? "" : ""}
            {data.content[k] ? " •" : ""}
          </button>
        ))}
      </div>
      {err && <ErrorBox error={err} />}
      <Field label="Title (optional)">
        <input className="ap-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Defaults to the approved page title" />
      </Field>
      <Field label="Content">
        <textarea className="ap-textarea" data-testid="ap-content-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="Write the approved content here…" />
      </Field>
      <button
        type="button" className="btn btn-solid btn-sm" data-testid="ap-content-save" disabled={busy}
        onClick={() => run(() => api.put(`/admin/content/${key}`, form), () => { toast.success("Content saved."); reload(); })}
      >
        {busy ? "Saving…" : `Save ${CONTENT_SECTIONS.find(([k]) => k === key)[1]}`}
      </button>
    </div>
  );
};

export const AdminNotifications = () => {
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ target: "user", email: "", emails: "", match_id: "", type: "general", title: "", body: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="ap-card">
      <h3 className="ap-card-title">Send Notification</h3>
      {err && <ErrorBox error={err} />}
      <div className="ap-row">
        <Field label="Target">
          <select className="ap-select" data-testid="ap-notif-target" value={form.target} onChange={(e) => set("target", e.target.value)}>
            <option value="user">Individual user</option>
            <option value="users">Selected users (comma-separated emails)</option>
            <option value="match">Match participants</option>
          </select>
        </Field>
        <Field label="Notification Type">
          <select className="ap-select" value={form.type} onChange={(e) => set("type", e.target.value)}>
            {NOTIFICATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      {form.target === "user" && <Field label="User Email"><input className="ap-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="user@example.com" /></Field>}
      {form.target === "users" && <Field label="Emails"><input className="ap-input" value={form.emails} onChange={(e) => set("emails", e.target.value)} placeholder="a@example.com, b@example.com" /></Field>}
      {form.target === "match" && <Field label="Match / Case ID"><input className="ap-input" value={form.match_id} onChange={(e) => set("match_id", e.target.value)} /></Field>}
      <Field label="Title"><input className="ap-input" data-testid="ap-notif-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
      <Field label="Message"><textarea className="ap-textarea" value={form.body} onChange={(e) => set("body", e.target.value)} /></Field>
      <button
        type="button" className="btn btn-solid btn-sm" data-testid="ap-notif-send" disabled={busy}
        onClick={() => run(() => api.post("/admin/notifications/send", form), (r) => { toast.success(`Sent to ${r.recipients} recipient(s).`); setForm((f) => ({ ...f, title: "", body: "" })); })}
      >
        {busy ? "Sending…" : "Send Notification"}
      </button>
    </div>
  );
};
